import { bisector } from 'd3-array'
import { scaleLinear, ScaleLinear } from 'd3-scale'
import { schemeCategory10, schemeSet3 } from 'd3-scale-chromatic'
import { throttle } from 'lodash'

import CanvasChart from '../common/CanvasChart'
import Legend, { LegendItem } from '../common/Legend'
import Tooltip from '../common/Tooltip'
import { RigstateItem, Span, Start } from './types'

const palette = [...schemeCategory10, ...schemeSet3]

// Hover state of a KPI/state segment: 70% of its Default color, no separate color token
// ("[Riser Colors] Design Intake & Decision", Confluence 5881724943, section 9.1).
const HOVER_OPACITY = 0.7

interface TooltipItem {
    description: string
    value: string
    color: string
}

type IndicatorGroup = {
    indicators: Map<string, any[]>
    colors: Map<string, string>
    title?: string
}

type State = {
    start: Start
    items: RigstateItem[]
    span?: Span
    groups: IndicatorGroup[]
    markers: Array<{ timestamp: number; title: string }>
}

export default class RigIndicatorsChart extends CanvasChart<State> {
    protected legend_: Legend
    protected legendRenderHoveredValues: (x: { text: string }[]) => void

    // Position under the cursor and the segments it falls in, plus the last render inputs, so a hover
    // change can redraw the canvas without a full widget render.
    private hoveredValue: number | null = null
    private hoveredKey = ''
    private lastCanvas: HTMLCanvasElement | null = null
    private lastScale: ScaleLinear<number, number> | null = null

    init(): void {
        super.init()
        this.el.classed(RigIndicatorsChart.name, true)
        this.legend_ = new Legend(this._legend.node())
        this.tooltip = new Tooltip()
        this.legendRenderHoveredValues = throttle((x: { text: string }[]) => this.legend_.renderHoveredValues(x), 100)
    }

    onMouseMove(mouse: [number, number], scale: ScaleLinear<number, number>): void {
        const data = this.subject.getValue()
        if (!data.groups?.length) return

        const x = mouse[this.isVertical ? 1 : 0]
        const value = scale.invert(x)
        const items = this.buildTooltipData(value, data)

        const contents = this._contents.node()
        if (contents) {
            const offset = contents.getBoundingClientRect()
            this.tooltip.setOffset({
                left: offset.left + (this.isVertical ? this.axisSize : 0),
                top: offset.top
            })
        }

        this.tooltip.render(items, undefined, [mouse[0], 0])

        this.setHovered(value, data)
    }

    onMouseLeave(): void {
        super.onMouseLeave()
        this.setHovered(null, this.subject.getValue())
    }

    // Redraws only when the cursor moves into a different segment, not on every mouse move.
    private setHovered(value: number | null, data: State): void {
        this.hoveredValue = value
        const key =
            value === null || !data?.groups?.length
                ? ''
                : data.groups.map(g => this.findSegmentStarts(g.indicators, value).join(',')).join('|')
        if (key === this.hoveredKey) return

        this.hoveredKey = key
        if (this.lastCanvas && this.lastScale) {
            this.renderCanvas(this.lastCanvas, data, this.lastScale)
        }
    }

    private findSegmentStarts(map: Map<string, any[]>, x: number): number[] {
        const bisectorValue = bisector((d: [number, number]) => d[0]).right
        const starts: number[] = []
        for (const serie of map.values()) {
            const index = bisectorValue(serie, x)
            const section = serie[index - 1]
            if (section && section[1] && index < serie.length) starts.push(section[0])
        }

        return starts
    }

    buildLegend(data: State): LegendItem[] {
        return data.groups.map((g, i) => ({
            text: g.title || `Set ${i + 1}`
        }))
    }

    buildTooltipData(x: number, data: State): TooltipItem[] {
        const bisectorValue = bisector((x: [number, number]) => x[0]).right

        const findActiveKey = (map: Map<string, any[]>): string | null => {
            for (const [key, serie] of map.entries()) {
                const index = bisectorValue(serie, x)
                if (index !== undefined) {
                    const section = serie[index - 1]
                    if (section && section[1]) return key
                }
            }

            return null
        }

        const items: TooltipItem[] = []
        data.groups.forEach((g, i) => {
            const active = findActiveKey(g.indicators)
            if (active) {
                items.push({
                    description: g.title || `Set ${i + 1}`,
                    value: active,
                    color: g.colors.get(active) ?? palette[i % palette.length]
                })
            }
        })

        return items
    }

    renderLegend(data: State): void {
        this._legend
            .style('height', 'auto')
            .style('width', `${this.legendSize}px`)
            .style('margin-bottom', `${this.axisSize}px`)

        if (data.groups?.length) {
            const legend = this.buildLegend(data)
            this.legend_.render(legend, false, this.legendSize, this.dashTheme || 'light')
        }
    }

    renderCanvas(canvas: HTMLCanvasElement, data: State, scale: ScaleLinear<number, number>): void {
        const ctx = canvas?.getContext('2d')
        if (!ctx) return
        this.lastCanvas = canvas
        this.lastScale = scale
        const { width, height } = canvas
        ctx.clearRect(0, 0, width, height)

        const spacing = 5
        const availableHeight = this.innerHeight - spacing * (data.groups.length - 1)
        const lineHeight = availableHeight / data.groups.length

        const drawSeries = (
            map: Map<string, any[]>,
            colorMap: Map<string, string>,
            offsetY: number
        ) => {
            const hovered = this.hoveredValue
            for (const [key, serie] of map.entries()) {
                ctx.fillStyle = colorMap.get(key) ?? '#999'
                for (let i = 0; i < serie.length - 1; i++) {
                    if (serie[i][1]) {
                        const isHovered = hovered !== null && hovered >= serie[i][0] && hovered < serie[i + 1][0]
                        ctx.globalAlpha = isHovered ? HOVER_OPACITY : 1
                        const x1 = scale(serie[i][0])
                        const x2 = scale(serie[i + 1][0])
                        ctx.fillRect(x1, offsetY, x2 - x1, lineHeight)
                    }
                }
            }
            ctx.globalAlpha = 1
        }

        data.groups.forEach((g, i) => {
            drawSeries(g.indicators, g.colors, i * (lineHeight + spacing))
        })

        if (data.markers?.length) {
            const domain = scale.domain()
            ctx.strokeStyle = '#000'
            ctx.lineWidth = 1
            ctx.setLineDash([3, 3])

            for (const marker of data.markers) {
                if (marker.timestamp >= domain[0] && marker.timestamp <= domain[1]) {
                    const x = scale(marker.timestamp)
                    ctx.beginPath()
                    ctx.moveTo(x, 0)
                    ctx.lineTo(x, this.innerHeight)
                    ctx.stroke()

                    ctx.font = '10px Arial'
                    const textWidth = ctx.measureText(marker.title).width
                    const badgeWidth = textWidth + 8
                    const badgeHeight = 16

                    ctx.fillStyle = '#000'
                    ctx.fillRect(x - badgeWidth / 2, 5, badgeWidth, badgeHeight)
                    ctx.fillStyle = '#fff'
                    ctx.textAlign = 'center'
                    ctx.fillText(marker.title, x, 16)
                }
            }

            ctx.setLineDash([])
        }
    }
}
